import React, { useState } from "react";

// AI User Behavior & CRO Intelligence Hub Component
// Visualizes:
// 1. What users are doing (clicks, scrolls, navigation paths, user personas, devices)
// 2. Average stay time and segment breakdowns
// 3. Why users are leaving (drop-off points, exits, bounce analysis)
// 4. Why users are not proceeding to plans (funnel leakage, commitment hesitation, OTP friction)
// 5. Actionable AI recommendations & conversion rate optimizations with free export & copy tools.

export default function AiBehaviorHub({
  insights,
  rangeMeta,
  site,
  onRefresh,
  isRefreshing,
  reAnalyzeToast,
  onDismissToast,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [recFilter, setRecFilter] = useState("all");
  const [copied, setCopied] = useState(false);
  const [showCompletedHistory, setShowCompletedHistory] = useState(false);
  const [statusOverrides, setStatusOverrides] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ownezzy_rec_status_overrides") || "{}");
    } catch (e) {
      return {};
    }
  });

  const toggleRecStatus = (id, currentCompleted) => {
    const newOverrides = { ...statusOverrides, [id]: !currentCompleted };
    setStatusOverrides(newOverrides);
    try {
      localStorage.setItem("ownezzy_rec_status_overrides", JSON.stringify(newOverrides));
    } catch (e) {}
  };

  if (!insights) return null;

  const {
    healthScore = 70,
    healthGrade = "Good",
    executiveVerdict = "",
    totals = {},
    behavior = {},
    stayTime = {},
    dropOffAndExits = {},
    funnelFriction = {},
    recommendations = [],
    analyzedAt = null,
  } = insights;

  // Base items from backend
  const baseCompleted = insights.completedRecommendations || [];
  const baseActive = (recommendations || []).filter((r) => !r.completed && r.status !== "completed");

  // Calculate active recommendations (strictly excludes completed ones)
  const activeRecs = baseActive.filter((r) => {
    if (statusOverrides[r.id] === true) return false; // marked done by user
    if (statusOverrides[r.id] === false) return true; // explicitly re-opened
    return !r.completed && r.status !== "completed";
  });

  // Calculate completed recommendations
  const userCompletedFromActive = baseActive.filter((r) => statusOverrides[r.id] === true);
  const completedRecs = [
    ...baseCompleted.filter((r) => statusOverrides[r.id] !== false),
    ...userCompletedFromActive,
  ];

  const allRecs = [...activeRecs, ...completedRecs];
  const totalOptimizations = baseCompleted.length + baseActive.length;

  // Copy Action Plan Tool
  const handleCopyActionPlan = () => {
    const lines = [
      `# Ownezzy AI Behavior & CRO Action Plan`,
      `Site: ${site || "All Websites"} · Period: ${rangeMeta}`,
      `Health Score: ${healthScore}/100 (${healthGrade})`,
      `Average Stay Time: ${stayTime.overallAvgStayFormatted || "–"}`,
      `Bounce Rate: ${totals.bounceRate || 0}%`,
      `Deployment Progress: ${completedRecs.length} of ${totalOptimizations} optimizations completed`,
      ``,
      `## Executive Summary`,
      executiveVerdict,
      ``,
      `## Top Friction Points (Why Users Do Not Proceed to Plans)`,
      ...(funnelFriction.frictionPoints || []).map(
        (f, i) => `${i + 1}. [${f.severity}] ${f.stage}: ${f.primaryObstacle} (Leakage: ${f.leakagePct}%)\n   Fix: ${f.recommendation}`
      ),
      ``,
      `## Active Next Steps (${activeRecs.length})`,
      ...activeRecs.map(
        (r, i) => `${i + 1}. [${r.priority}] ${r.title} (${r.expectedLift})\n   - Finding: ${r.observation}\n   - Implementation: ${r.action}`
      ),
      ``,
      `## Completed & Live CRO Enhancements (${completedRecs.length})`,
      ...completedRecs.map(
        (r, i) => `${i + 1}. [DONE] ${r.title} (${r.verifiedLift || "Live in Production"})\n   - Verified: ${r.implementedNote || r.action}`
      ),
    ];

    navigator.clipboard?.writeText(lines.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  // Download AI Report Tool (Free Export Tool)
  const handleDownloadReport = () => {
    const reportData = {
      title: "Ownezzy AI User Behavior & CRO Analysis",
      generatedAt: analyzedAt || new Date().toISOString(),
      scope: site || "all_sites",
      period: rangeMeta,
      insights: {
        ...insights,
        completedRecommendations: completedRecs,
        activeRecommendations: activeRecs,
        optimizationStats: {
          total: totalOptimizations,
          completed: completedRecs.length,
          active: activeRecs.length,
          completionRatePct: Math.round((completedRecs.length / Math.max(1, totalOptimizations)) * 100),
        },
      },
    };
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ownezzy-ai-analysis-${site || "all"}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter recommendations (strictly for active items)
  const filteredRecs = activeRecs.filter((r) => {
    if (recFilter === "all") return true;
    if (recFilter === "critical") return r.priority === "Critical";
    if (recFilter === "quickWin") return r.effort === "Quick Win";
    if (recFilter === "highImpact") return r.priority === "Critical" || r.priority === "High";
    return true;
  });

  // Calculate SVG circular gauge meter
  const strokeDash = 251.2; // 2 * PI * 40
  const scoreOffset = strokeDash - (strokeDash * Math.min(100, Math.max(0, healthScore))) / 100;
  const scoreColor =
    healthScore >= 80 ? "#10b981" : healthScore >= 65 ? "#3b82f6" : healthScore >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <section className="panel tab-gap ai-hub-panel">
      {/* Panel Header with Score & Free Tools */}
      <div className="ai-hub-header">
        <div className="ai-hub-title-area">
          <div className="ai-hub-badge">
            <span className="ai-spark-icon">✦</span>
            <span>AI CRO &amp; BEHAVIOR INTELLIGENCE</span>
          </div>
          <h2>User Behavior &amp; Conversion Diagnostics</h2>
          <p>
            Algorithmic analysis of visitor journeys, engagement velocity, exit drop-offs, and plan hesitation for{" "}
            <b>{site ? site : "all websites"}</b> ({rangeMeta}).
          </p>
        </div>

        <div className="ai-hub-score-box">
          <div className="ai-gauge-wrapper">
            <svg className="ai-gauge-svg" width="94" height="94" viewBox="0 0 100 100">
              <circle className="ai-gauge-bg" cx="50" cy="50" r="40" strokeWidth="8" />
              <circle
                className="ai-gauge-fill"
                cx="50"
                cy="50"
                r="40"
                strokeWidth="8"
                stroke={scoreColor}
                strokeDasharray={strokeDash}
                strokeDashoffset={scoreOffset}
              />
            </svg>
            <div className="ai-gauge-val">
              <strong>{healthScore}</strong>
              <small>/100</small>
            </div>
          </div>
          <div className="ai-score-label">
            <span className={`ai-grade-tag grade-${healthGrade.toLowerCase().replace(/\s+/g, "-")}`}>
              {healthGrade}
            </span>
            <small>Experience Health</small>
          </div>
        </div>
      </div>

      {/* Free Interactive Action Tools Bar */}
      <div className="ai-tools-bar">
        <div className="ai-verdict-banner">
          <span className="verdict-icon">💡</span>
          <div className="verdict-content">
            <b>AI Executive Diagnosis:</b>
            <span>{executiveVerdict}</span>
          </div>
        </div>

        <div className="ai-tools-actions">
          {analyzedAt && (
            <span className="ai-live-stamp" title="Heuristic engine re-analyzed timestamp">
              ● Live Engine: {new Date(analyzedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button
            type="button"
            className="ai-tool-btn primary"
            onClick={handleCopyActionPlan}
            title="Copy actionable optimization plan formatted in Markdown"
          >
            {copied ? "✓ Action Plan Copied!" : "📋 Copy Action Plan"}
          </button>
          <button
            type="button"
            className="ai-tool-btn"
            onClick={handleDownloadReport}
            title="Download full analysis as JSON"
          >
            📥 Export Report
          </button>
          {onRefresh && (
            <button
              type="button"
              className="ai-tool-btn"
              onClick={onRefresh}
              disabled={isRefreshing}
              title="Re-run heuristic AI engine with latest live data"
            >
              {isRefreshing ? "Analyzing Telemetry..." : "🔄 Re-Analyze"}
            </button>
          )}
        </div>
      </div>

      {/* Interactive Tabs */}
      <div className="ai-tabs-nav">
        {[
          { key: "overview", label: "Overview & Summary", icon: "📊" },
          { key: "behavior", label: "What Users Are Doing", icon: "👀" },
          { key: "stayTime", label: "Average Stay Time", icon: "⏱" },
          { key: "leaving", label: "Why They Are Leaving", icon: "🚪" },
          { key: "plans", label: "Why Not Proceeding to Plans", icon: "🛑" },
          {
            key: "recommendations",
            label: `Recommendations (${activeRecs.length})`,
            icon: "⚡",
          },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`ai-tab-btn ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab 1: Overview & AI Summary */}
      {activeTab === "overview" && (
        <div className="ai-tab-pane">
          <div className="ai-kpi-grid">
            <div className="ai-kpi-card">
              <span className="kpi-label">AVERAGE STAY TIME</span>
              <div className="kpi-val-row">
                <strong className="kpi-val">{totals.overallAvgStayFormatted || "–"}</strong>
                <span className="kpi-sub">Median: {totals.medianStayFormatted || "–"}</span>
              </div>
              <p className="kpi-desc">
                {totals.overallAvgStaySec >= 90
                  ? "✓ Healthy session duration with deep catalog exploration."
                  : "⚠ Shorter than ideal; visitors exit before full engagement."}
              </p>
            </div>

            <div className="ai-kpi-card">
              <span className="kpi-label">ACTION INTENSITY</span>
              <div className="kpi-val-row">
                <strong className="kpi-val">{totals.avgActionsPerSession}</strong>
                <span className="kpi-sub">actions / session</span>
              </div>
              <p className="kpi-desc">
                Based on {totals.activitiesAnalyzed} recorded clicks, scrolls, and navigations.
              </p>
            </div>

            <div className="ai-kpi-card">
              <span className="kpi-label">BOUNCE &amp; EXIT RATE</span>
              <div className="kpi-val-row">
                <strong className={`kpi-val ${totals.bounceRate > 45 ? "val-danger" : "val-safe"}`}>
                  {totals.bounceRate}%
                </strong>
                <span className="kpi-sub">Left under 15 sec</span>
              </div>
              <p className="kpi-desc">
                {totals.bounceRate > 45
                  ? "High initial bounce. Visitors leave before reaching the offer."
                  : "✓ Good retention rate across store templates."}
              </p>
            </div>

            <div className="ai-kpi-card">
              <span className="kpi-label">PLAN PROGRESSION</span>
              <div className="kpi-val-row">
                <strong className="kpi-val val-info">
                  {funnelFriction.planProgressionRate || 0}%
                </strong>
                <span className="kpi-sub">of modal opens</span>
              </div>
              <p className="kpi-desc">
                Drop-off rate at Step 0: <b>{funnelFriction.planDropOffRate || 0}%</b>.
              </p>
            </div>
          </div>

          {/* CRO Implementation Progress Banner */}
          <div className="ai-progress-box">
            <div className="ai-progress-info">
              <div className="ai-progress-title-row">
                <span className="ai-progress-badge">CRO DEPLOYMENT STATUS</span>
                <strong>{completedRecs.length} of {allRecs.length} Analyst Optimizations Deployed</strong>
                <span className="ai-progress-pct">{Math.round((completedRecs.length / Math.max(1, allRecs.length)) * 100)}% Complete</span>
              </div>
              <p>
                {completedRecs.length} verified CRO enhancements (14-day free trial golden highlight, exit-intent demo prompt, movable floating card, mobile card layout, and 5 high-demand store niches) are deployed to production.
              </p>
            </div>
            <div className="ai-progress-bar">
              <div
                className="ai-progress-fill"
                style={{ width: `${Math.round((completedRecs.length / Math.max(1, allRecs.length)) * 100)}%` }}
              />
            </div>
          </div>

          <div className="ai-dual-grid">
            <div className="ai-subpanel">
              <h3 className="ai-subhead">🎯 Key Behavioral Findings</h3>
              <ul className="ai-bullet-list">
                <li>
                  <b>User Engagement:</b> {behavior.personas?.storeExplorer?.pct || 0}% of visitors explore store catalogs and products extensively, but only {funnelFriction.modalOpenRate || 0}% open the website creation flow.
                </li>
                <li>
                  <b>Stay Time Gap:</b> Users who proceed to select plans stay <b>{stayTime.segments?.[0]?.formatted || "2.5x longer"}</b> than casual store skimmers ({stayTime.segments?.[2]?.formatted || "short"}).
                </li>
                <li>
                  <b>Drop-off Cause:</b> {dropOffAndExits.exitReasons?.[1]?.percentage || 0}% of offer modal opens exit via the cross button on Step 0 without selecting a plan.
                </li>
              </ul>
            </div>

            <div className="ai-subpanel">
              <div className="ai-subpanel-header">
                <h3 className="ai-subhead">⚡ Top Recommended Growth Actions</h3>
                <span className="badge-pending">{activeRecs.length} Active Next Steps</span>
              </div>
              <p className="ai-subnote">Actionable CRO steps prioritized for conversion velocity.</p>
              <div className="ai-quick-rec-list">
                {(activeRecs.length > 0 ? activeRecs : allRecs).slice(0, 3).map((r) => (
                  <div key={r.id} className="ai-quick-rec-item">
                    <span className={`priority-tag ${r.priority.toLowerCase().replace(/\s+/g, "-")}`}>{r.priority}</span>
                    <div className="quick-rec-body">
                      <b>{r.title}</b>
                      <small>{r.expectedLift}</small>
                    </div>
                  </div>
                ))}
              </div>

              {completedRecs.length > 0 && (
                <div
                  className="ai-completed-summary-bar"
                  onClick={() => { setActiveTab("recommendations"); setRecFilter("completed"); }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="comp-bar-left">
                    <span className="comp-check">✓</span>
                    <span><b>{completedRecs.length} analyst recommendations</b> implemented &amp; live in production</span>
                  </div>
                  <span className="view-link">View Implemented →</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: What Users Are Doing (Behavior Breakdown) */}
      {activeTab === "behavior" && (
        <div className="ai-tab-pane">
          <div className="ai-dual-grid">
            {/* Interaction Breakdown */}
            <div className="ai-subpanel">
              <h3 className="ai-subhead">🖱 Actions Breakdown</h3>
              <p className="ai-subnote">Distribution of user actions captured across browsing sessions.</p>
              <div className="ai-bar-meter-group">
                <div className="ai-bar-meter">
                  <div className="meter-label-row">
                    <span>Clicks ({behavior.actionBreakdown?.clicks || 0})</span>
                    <b>{behavior.actionBreakdown?.clicksPct || 0}%</b>
                  </div>
                  <div className="meter-track">
                    <span
                      className="meter-fill fill-blue"
                      style={{ width: `${behavior.actionBreakdown?.clicksPct || 0}%` }}
                    />
                  </div>
                </div>

                <div className="ai-bar-meter">
                  <div className="meter-label-row">
                    <span>Scroll Depth ({behavior.actionBreakdown?.scrolls || 0})</span>
                    <b>{behavior.actionBreakdown?.scrollsPct || 0}%</b>
                  </div>
                  <div className="meter-track">
                    <span
                      className="meter-fill fill-pink"
                      style={{ width: `${behavior.actionBreakdown?.scrollsPct || 0}%` }}
                    />
                  </div>
                </div>

                <div className="ai-bar-meter">
                  <div className="meter-label-row">
                    <span>Page Navigations ({behavior.actionBreakdown?.navigations || 0})</span>
                    <b>{behavior.actionBreakdown?.navigationsPct || 0}%</b>
                  </div>
                  <div className="meter-track">
                    <span
                      className="meter-fill fill-green"
                      style={{ width: `${behavior.actionBreakdown?.navigationsPct || 0}%` }}
                    />
                  </div>
                </div>
              </div>

              <h4 className="ai-minihead" style={{ marginTop: "20px" }}>
                📱 Device Distribution
              </h4>
              <div className="ai-device-tags">
                <span className="dev-tag">
                  📱 Mobile: <b>{behavior.devices?.mobilePct || 0}%</b> ({behavior.devices?.mobile || 0})
                </span>
                <span className="dev-tag">
                  💻 Desktop: <b>{behavior.devices?.desktopPct || 0}%</b> ({behavior.devices?.desktop || 0})
                </span>
                <span className="dev-tag">
                  📟 Tablet: <b>{behavior.devices?.tablet || 0}</b>
                </span>
              </div>
            </div>

            {/* User Personas */}
            <div className="ai-subpanel">
              <h3 className="ai-subhead">👥 User Behavior Personas</h3>
              <p className="ai-subnote">Clustered by interaction depth, scroll progress, and intent.</p>
              <div className="ai-persona-cards">
                <div className="persona-card high-intent">
                  <div className="persona-head">
                    <span className="persona-icon">🚀</span>
                    <b>High-Intent Buyers</b>
                    <span className="persona-pct">{behavior.personas?.highIntent?.pct || 0}%</span>
                  </div>
                  <p>Opened offer modal, selected plans, or attempted OTP verification.</p>
                </div>

                <div className="persona-card explorer">
                  <div className="persona-head">
                    <span className="persona-icon">🔍</span>
                    <b>Store Explorers</b>
                    <span className="persona-pct">{behavior.personas?.storeExplorer?.pct || 0}%</span>
                  </div>
                  <p>Clicked 3+ products, scrolled &gt;50% depth, but did not open the offer.</p>
                </div>

                <div className="persona-card window">
                  <div className="persona-head">
                    <span className="persona-icon">👀</span>
                    <b>Window Shoppers</b>
                    <span className="persona-pct">{behavior.personas?.windowShopper?.pct || 0}%</span>
                  </div>
                  <p>Casual skimmers viewing the homepage banner without deeper interaction.</p>
                </div>

                <div className="persona-card bouncer">
                  <div className="persona-head">
                    <span className="persona-icon">⚡</span>
                    <b>Instant Bouncers</b>
                    <span className="persona-pct">{behavior.personas?.instantBouncer?.pct || 0}%</span>
                  </div>
                  <p>Left in under 15 seconds with 0–1 click.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Clicks & Scroll Depth */}
          <div className="ai-dual-grid" style={{ marginTop: "16px" }}>
            <div className="ai-subpanel">
              <h3 className="ai-subhead">🔥 Top Clicked UI Elements</h3>
              <div className="ai-ranked-list">
                {(behavior.topClicks || []).map((c, i) => (
                  <div key={i} className="ranked-item">
                    <span className="rank-num">{i + 1}</span>
                    <span className="rank-target">{c.target}</span>
                    <span className="rank-count">{c.count} clicks ({c.pct}%)</span>
                  </div>
                ))}
                {!behavior.topClicks?.length && <p className="empty-note">No click records yet.</p>}
              </div>
            </div>

            <div className="ai-subpanel">
              <h3 className="ai-subhead">📜 Scroll Depth Reach</h3>
              <p className="ai-subnote">Percentage of visitors reaching page depth milestones.</p>
              <div className="scroll-milestones-grid">
                {[
                  { m: "25%", pct: behavior.scrollDepths?.reached25Pct || 0 },
                  { m: "50%", pct: behavior.scrollDepths?.reached50Pct || 0 },
                  { m: "75%", pct: behavior.scrollDepths?.reached75Pct || 0 },
                  { m: "100%", pct: behavior.scrollDepths?.reached100Pct || 0 },
                ].map((s) => (
                  <div key={s.m} className="milestone-box">
                    <span className="m-label">{s.m} Depth</span>
                    <strong className="m-val">{s.pct}%</strong>
                    <div className="m-track">
                      <span className="m-fill" style={{ width: `${s.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Average Stay Time & Segments */}
      {activeTab === "stayTime" && (
        <div className="ai-tab-pane">
          <div className="ai-stay-hero">
            <div className="stay-hero-metric">
              <span>Overall Average Stay Time</span>
              <strong>{stayTime.overallAvgStayFormatted || "–"}</strong>
              <small>Median: {stayTime.medianStayFormatted || "–"}</small>
            </div>
            <div className="stay-hero-note">
              <b>⏱ AI Stay Time Analysis:</b>
              <p>
                Visitors who proceed to choose a plan spend significantly longer on the site, demonstrating high
                consideration. Optimizing hero discovery reduces bounce and lifts average stay time by up to 45 seconds.
              </p>
            </div>
          </div>

          <h3 className="ai-subhead" style={{ marginTop: "24px" }}>
            📊 Stay Time Comparison by Visitor Intent
          </h3>
          <div className="ai-segment-grid">
            {(stayTime.segments || []).map((seg, i) => (
              <div key={i} className="ai-segment-card">
                <span className="seg-name">{seg.segment}</span>
                <strong className="seg-time">{seg.formatted}</strong>
                <span className="seg-count">{seg.count} sessions</span>
                <p className="seg-desc">{seg.insight}</p>
              </div>
            ))}
          </div>

          <h3 className="ai-subhead" style={{ marginTop: "24px" }}>
            ⏳ Stay Time Distribution
          </h3>
          <div className="stay-distribution-bar">
            {Object.entries(stayTime.distribution || {}).map(([key, item]) => (
              <div key={key} className={`dist-segment dist-${key}`} style={{ flex: Math.max(8, item.pct) }}>
                <span className="dist-label">{item.label}</span>
                <b>{item.pct}%</b>
                <small>({item.count})</small>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 4: Why Users Leave (Drop-off & Exit Diagnostics) */}
      {activeTab === "leaving" && (
        <div className="ai-tab-pane">
          <div className="ai-dual-grid">
            <div className="ai-subpanel">
              <h3 className="ai-subhead">🚪 Exit Mode Breakdown</h3>
              <p className="ai-subnote">How visitors departed from the store.</p>
              <div className="ai-exit-mode-cards">
                <div className="exit-mode-card">
                  <span className="exit-icon">📱</span>
                  <div className="exit-mode-data">
                    <strong>{dropOffAndExits.exitTypeLeft || 0} Exits</strong>
                    <span>Left / Switched Tab ({dropOffAndExits.totalExits > 0 ? Math.round((dropOffAndExits.exitTypeLeft / dropOffAndExits.totalExits) * 100) : 0}%)</span>
                  </div>
                </div>
                <div className="exit-mode-card">
                  <span className="exit-icon">❌</span>
                  <div className="exit-mode-data">
                    <strong>{dropOffAndExits.exitTypeClosed || 0} Exits</strong>
                    <span>Closed Tab / Browser ({dropOffAndExits.totalExits > 0 ? Math.round((dropOffAndExits.exitTypeClosed / dropOffAndExits.totalExits) * 100) : 0}%)</span>
                  </div>
                </div>
              </div>

              <h4 className="ai-minihead" style={{ marginTop: "20px" }}>
                📍 Top Exit Pages / Paths
              </h4>
              <div className="ai-ranked-list">
                {(dropOffAndExits.topExitPages || []).map((p, i) => (
                  <div key={i} className="ranked-item">
                    <span className="rank-num">{i + 1}</span>
                    <span className="rank-target">{p.path}</span>
                    <span className="rank-count">{p.count} exits ({p.pct}%)</span>
                  </div>
                ))}
                {!dropOffAndExits.topExitPages?.length && (
                  <p className="empty-note">No exit page logs captured yet.</p>
                )}
              </div>
            </div>

            <div className="ai-subpanel">
              <h3 className="ai-subhead">🔍 Diagnosed Exit Causes</h3>
              <div className="ai-exit-reasons-list">
                {(dropOffAndExits.exitReasons || []).map((r, i) => (
                  <div key={i} className={`exit-reason-card sev-${r.severity.toLowerCase()}`}>
                    <div className="reason-top">
                      <span className={`reason-sev-tag ${r.severity.toLowerCase()}`}>{r.severity}</span>
                      <b>{r.reason}</b>
                      <strong className="reason-pct">{r.percentage}%</strong>
                    </div>
                    <p className="reason-desc">{r.description}</p>
                    <div className="reason-fix">
                      <span>💡 Suggested Countermeasure:</span> {r.solution}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 5: Why Not Proceeding to Plans (Friction Points) */}
      {activeTab === "plans" && (
        <div className="ai-tab-pane">
          {/* Visual Funnel Pipeline */}
          <div className="funnel-pipeline-visual">
            <h3 className="ai-subhead">📉 Live Funnel Progression &amp; Leakage Pipeline</h3>
            <div className="pipeline-steps">
              <div className="pipe-step">
                <span className="step-badge">1. VISITED</span>
                <strong>{totals.sessionsAnalyzed || 0}</strong>
                <small>Store Visitors</small>
              </div>

              <div className="pipe-arrow">
                <span className="drop-tag">
                  -{Math.max(0, 100 - (funnelFriction.modalOpenRate || 0))}% Drop
                </span>
                <span className="arrow-line">→</span>
              </div>

              <div className="pipe-step">
                <span className="step-badge">2. OPENED OFFER</span>
                <strong>{funnelFriction.modalOpenRate || 0}%</strong>
                <small>Clicked "Get it for free"</small>
              </div>

              <div className="pipe-arrow">
                <span className="drop-tag">
                  -{funnelFriction.planDropOffRate || 0}% Drop
                </span>
                <span className="arrow-line">→</span>
              </div>

              <div className="pipe-step highlight">
                <span className="step-badge">3. CHOSE PLAN</span>
                <strong>{funnelFriction.planProgressionRate || 0}%</strong>
                <small>Selected Trial or Paid</small>
              </div>

              <div className="pipe-arrow">
                <span className="drop-tag">
                  -{funnelFriction.otpDropOffRate || 0}% Drop
                </span>
                <span className="arrow-line">→</span>
              </div>

              <div className="pipe-step success">
                <span className="step-badge">4. OTP VERIFIED</span>
                <strong>{funnelFriction.otpProgressionRate || 0}%</strong>
                <small>Verified Phone &amp; Email</small>
              </div>
            </div>
          </div>

          <h3 className="ai-subhead" style={{ marginTop: "24px" }}>
            🛑 Root-Cause Friction Diagnostics
          </h3>
          <div className="friction-points-list">
            {(funnelFriction.frictionPoints || []).map((f, i) => (
              <div key={i} className={`friction-card sev-${f.severity.toLowerCase()}`}>
                <div className="friction-header">
                  <div className="friction-title-group">
                    <span className={`friction-sev-badge ${f.severity.toLowerCase()}`}>{f.severity}</span>
                    <b>{f.stage}</b>
                  </div>
                  <div className="friction-leakage-badge">
                    Leakage: <b>{f.leakagePct}%</b>
                  </div>
                </div>

                <div className="friction-body">
                  <div className="friction-obstacle">
                    <span>Primary Obstacle:</span> <b>{f.primaryObstacle}</b>
                  </div>
                  <p className="friction-explanation">{f.explanation}</p>
                  <div className="friction-recommendation">
                    <span className="rec-icon">⚡</span>
                    <div>
                      <b>Recommended Fix:</b>
                      <p>{f.recommendation}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 6: Actionable AI Recommendations */}
      {activeTab === "recommendations" && (
        <div className="ai-tab-pane">
          <div className="recs-filter-bar">
            <span>Filter Active Steps:</span>
            {[
              { key: "all", label: `All Active (${activeRecs.length})` },
              { key: "critical", label: "Critical Priority" },
              { key: "highImpact", label: "High Impact" },
              { key: "quickWin", label: "Quick Wins (Low Effort)" },
            ].map((f) => (
              <button
                key={f.key}
                type="button"
                className={`filter-btn ${recFilter === f.key ? "active" : ""}`}
                onClick={() => setRecFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {filteredRecs.length === 0 ? (
            <div className="ai-empty-recs">
              <span className="empty-sparkle">🎉</span>
              <h3>All Analyst Recommendations Completed!</h3>
              <p>
                Every recommended conversion rate optimization has been successfully executed and deployed to production.
              </p>
            </div>
          ) : (
            <div className="ai-recs-grid">
              {filteredRecs.map((rec) => (
                <div
                  key={rec.id}
                  className={`ai-rec-card priority-${rec.priority.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="rec-card-top">
                    <div className="rec-badge-group">
                      <span className={`priority-pill ${rec.priority.toLowerCase().replace(/\s+/g, "-")}`}>
                        {rec.priority}
                      </span>
                      <span className="category-pill">{rec.category}</span>
                      <span className="effort-pill">{rec.effort}</span>
                    </div>
                    <span className="expected-lift-tag">{rec.expectedLift}</span>
                  </div>

                  <h3 className="rec-title">{rec.title}</h3>

                  <div className="rec-obs">
                    <span>Data Finding:</span> {rec.observation}
                  </div>

                  <div className="rec-action-box">
                    <b>Actionable Implementation:</b>
                    <p>{rec.action}</p>
                  </div>

                  <div className="rec-card-footer">
                    <button
                      type="button"
                      className="rec-toggle-btn btn-mark"
                      onClick={() => toggleRecStatus(rec.id, false)}
                      title="Mark as done and remove from recommendations list"
                    >
                      ✓ Mark as Done &amp; Remove
                    </button>
                    <span className="rec-pending-badge">⚡ Action Pending</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Completed Optimizations History (Archived below, not in recommendations) */}
          {completedRecs.length > 0 && (
            <div className="ai-completed-history-section">
              <button
                type="button"
                className="ai-toggle-history-btn"
                onClick={() => setShowCompletedHistory((prev) => !prev)}
              >
                <div className="history-btn-left">
                  <span className="history-check-icon">✓</span>
                  <span><b>{completedRecs.length} Analyst Recommendations Implemented</b> (Archived in Production)</span>
                </div>
                <span className="history-toggle-indicator">
                  {showCompletedHistory ? "▲ Hide Deployed Archive" : "▼ View Deployed Archive"}
                </span>
              </button>

              {showCompletedHistory && (
                <div className="ai-completed-history-grid">
                  {completedRecs.map((rec) => (
                    <div key={rec.id} className="ai-completed-history-card">
                      <div className="history-card-top">
                        <span className="history-badge">✓ Implemented</span>
                        <span className="history-lift">{rec.verifiedLift || "Live in Production"}</span>
                      </div>
                      <h4 className="history-title">{rec.title}</h4>
                      <p className="history-note">{rec.implementedNote || rec.action}</p>
                      {statusOverrides[rec.id] && (
                        <button
                          type="button"
                          className="history-reopen-btn"
                          onClick={() => toggleRecStatus(rec.id, true)}
                        >
                          ↩ Reopen to Active List
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
